/**
 * TableInsertModal — sélecteur de dimensions pour l'insertion d'un tableau.
 *
 * UX :
 *   - Grille de prévisualisation 10×10 : survoler pour choisir les dimensions,
 *     cliquer pour confirmer. C'est l'interaction principale.
 *   - Deux champs numériques en-dessous, pour saisie directe (utile pour
 *     dépasser le visuel et atteindre les limites 10 cols × 20 lignes).
 *   - Boutons Annuler / Insérer.
 *
 * Contraintes du doc : max 10 colonnes × 20 lignes.
 */

import { useEffect, useRef, useState } from "react";

const MAX_COLS = 10;
const MAX_ROWS = 20;
const GRID_VISIBLE_ROWS = 10; // ce que la grille de hover affiche
const GRID_VISIBLE_COLS = 10;

export interface TableInsertModalProps {
  open: boolean;
  onConfirm: (rows: number, columns: number) => void;
  onCancel: () => void;
}

export function TableInsertModal({ open, onConfirm, onCancel }: TableInsertModalProps) {
  // Dimensions courantes (visibles dans les inputs et la grille)
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  // Cellule survolée — pilote l'affichage temporaire de la grille
  const [hover, setHover] = useState<{ row: number; col: number } | null>(null);

  const firstInputRef = useRef<HTMLInputElement>(null);

  // Reset à chaque ouverture
  useEffect(() => {
    if (open) {
      setRows(3);
      setCols(3);
      setHover(null);
      const timer = setTimeout(() => firstInputRef.current?.focus(), 30);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  // Échap = annuler
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(clamp(rows, 1, MAX_ROWS), clamp(cols, 1, MAX_COLS));
  };

  // Ce qu'on met en surbrillance : hover prioritaire, sinon valeurs des inputs
  const previewRows = hover ? hover.row : Math.min(rows, GRID_VISIBLE_ROWS);
  const previewCols = hover ? hover.col : Math.min(cols, GRID_VISIBLE_COLS);

  return (
    <div
      className="rte-link-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <form className="rte-link-modal" onSubmit={handleSubmit}>
        <div className="rte-link-modal-title">Insérer un tableau</div>

        {/* Grille de hover */}
        <div className="rte-table-grid-wrapper">
          <div
            className="rte-table-grid"
            style={{
              gridTemplateColumns: `repeat(${GRID_VISIBLE_COLS}, 1fr)`,
            }}
            onMouseLeave={() => setHover(null)}
          >
            {Array.from({ length: GRID_VISIBLE_ROWS * GRID_VISIBLE_COLS }).map((_, i) => {
              const row = Math.floor(i / GRID_VISIBLE_COLS) + 1;
              const col = (i % GRID_VISIBLE_COLS) + 1;
              const isFilled = row <= previewRows && col <= previewCols;
              return (
                <button
                  key={i}
                  type="button"
                  className={"rte-table-grid-cell" + (isFilled ? " is-filled" : "")}
                  onMouseEnter={() => {
                    setHover({ row, col });
                    setRows(row);
                    setCols(col);
                  }}
                  onClick={() => onConfirm(row, col)}
                  aria-label={`Insérer ${col} colonnes × ${row} lignes`}
                />
              );
            })}
          </div>
          <div className="rte-table-grid-caption">
            {previewCols} colonne{previewCols > 1 ? "s" : ""} × {previewRows} ligne
            {previewRows > 1 ? "s" : ""}
          </div>
        </div>

        {/* Saisie directe pour dépasser les 10 lignes visibles */}
        <div className="rte-table-inputs">
          <div className="rte-table-input-field">
            <label htmlFor="rte-table-rows">Lignes</label>
            <input
              id="rte-table-rows"
              ref={firstInputRef}
              type="number"
              min={1}
              max={MAX_ROWS}
              value={rows}
              onChange={(e) => setRows(clamp(parseInt(e.target.value, 10) || 1, 1, MAX_ROWS))}
            />
            <span className="rte-table-input-max">max {MAX_ROWS}</span>
          </div>
          <div className="rte-table-input-field">
            <label htmlFor="rte-table-cols">Colonnes</label>
            <input
              id="rte-table-cols"
              type="number"
              min={1}
              max={MAX_COLS}
              value={cols}
              onChange={(e) => setCols(clamp(parseInt(e.target.value, 10) || 1, 1, MAX_COLS))}
            />
            <span className="rte-table-input-max">max {MAX_COLS}</span>
          </div>
        </div>

        <div className="rte-link-modal-actions">
          <button type="button" className="rte-link-modal-btn-ghost" onClick={onCancel}>
            Annuler
          </button>
          <button type="submit" className="rte-link-modal-btn-primary">
            Insérer
          </button>
        </div>
      </form>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
